import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Tiptap → React (read-only)
 *
 * Renders a `TiptapDocument` to React elements without pulling in the
 * Tiptap editor or ProseMirror. Used by `TextBlockView` (and any future
 * read-only surface that needs to display rich-text memory bodies — block
 * detail modal, search snippet expansion, exports, etc.).
 *
 * Why a hand-rolled walker rather than `@tiptap/html` + `generateHTML`:
 *
 *   - The viewer renders one `TextBlockView` for *every* text card on the
 *     stream — potentially hundreds at once. Pulling in Tiptap + StarterKit
 *     + the entire ProseMirror schema for a read-only render path adds
 *     150–200KB gzipped to the initial bundle for no functional gain.
 *   - We control the editor's extension list (StarterKit + Link in v1) so
 *     the schema this renderer needs to know about is finite and obvious.
 *   - `dangerouslySetInnerHTML` (which `generateHTML` would feed into) is
 *     a sharper edge than direct React elements; emitting nodes lets
 *     React's reconciler do its normal job and keeps the surface
 *     accessible to test selectors.
 *
 * Trade-off: every new node/mark type the editor learns must also be
 * taught here, or it'll render as a transparent fragment (children emit,
 * structure is lost). The set is intentionally small in v1 and tracked
 * in the rich-text-omnibar plan's "Open Questions".
 *
 * Unknown nodes degrade gracefully: their `content` children are walked
 * normally, so a memory captured against a future schema still shows its
 * text content even on a stale viewer build.
 */
import React from "react";
import { isSafeLinkHref } from "@shared/types/tiptap-document";
/**
 * Renders a Tiptap document inside a single wrapping `<div>`. The wrapper
 * exists so consumers can target the rich content with CSS without having
 * to query for `<p>` / `<h1>` / etc. directly on the card surface.
 */
export const TiptapRender = ({ doc, className, onToggleTask }) => {
    // Counter passed through the walk so the Nth task item the renderer
    // visits is tagged with `index === N`. `useRef`/state would over-fire
    // — the counter is intentionally per-render and resets on every paint
    // so it stays in lockstep with the `doc` shape currently on screen.
    const ctx = { taskCounter: { value: 0 }, onToggleTask };
    return (_jsx("div", { className: className, children: (doc.content ?? []).map((node, i) => renderNode(node, i, ctx)) }));
};
// ---------------------------------------------------------------------------
// Node renderer (block-level + inline)
// ---------------------------------------------------------------------------
/**
 * Walks a single ProseMirror node into a React element. The `key` is the
 * caller's array index — fine here because the doc is immutable in
 * read-only mode and we never reorder children mid-render.
 */
function renderNode(node, key, ctx) {
    if (node.type === "text") {
        return renderTextWithMarks(node, key);
    }
    if (node.type === "hardBreak") {
        return _jsx("br", {}, key);
    }
    if (node.type === "horizontalRule") {
        return _jsx("hr", {}, key);
    }
    // taskItem allocates its index BEFORE walking children so the parent's
    // ordinal sits above any nested task items it contains. Other nodes
    // recurse first; the children-then-self vs self-then-children split
    // doesn't matter for them since they don't tag themselves with an
    // index. Pulling the index out here (rather than inside the switch's
    // `taskItem` arm) keeps the `children` line below stable across all
    // node types.
    const taskIndex = node.type === "taskItem" ? ctx.taskCounter.value++ : undefined;
    const children = (node.content ?? []).map((child, i) => renderNode(child, i, ctx));
    switch (node.type) {
        case "paragraph":
            // An empty paragraph is the user's "blank line between thoughts" —
            // they hit Enter on an empty line. The Tiptap editor paints these
            // with visible 1lh height by injecting a `ProseMirror-trailingBreak`
            // `<br>` into the contenteditable at render time; the doc JSON it
            // emits is just `{ type: "paragraph" }` with no children. The
            // read-only walker has to do the same `<br>` injection itself,
            // otherwise an empty `<p></p>` collapses to zero height (CSS sets
            // `p { margin: 0 }` and there's no content) and the user's blank
            // line reads as just a paragraph-sibling 6px gap rather than a
            // full visible break — `[para("a"), para(), para("b")]` paints as
            // "a, b" instead of "a, blank line, b".
            if (isEmptyParagraph(node)) {
                return (_jsx("p", { children: _jsx("br", {}) }, key));
            }
            return _jsx("p", { children: children }, key);
        case "heading": {
            // Cap the level at 1–3 (StarterKit's default range in our editor
            // config). A doc that smuggles in `level: 6` from a richer source
            // shouldn't paint <h6> on a memory card.
            const rawLevel = typeof node.attrs?.level === "number" ? node.attrs.level : 1;
            const level = Math.min(Math.max(rawLevel, 1), 3);
            return level === 1 ? (_jsx("h1", { children: children }, key)) : level === 2 ? (_jsx("h2", { children: children }, key)) : (_jsx("h3", { children: children }, key));
        }
        case "bulletList":
            return _jsx("ul", { children: children }, key);
        case "orderedList": {
            // Preserve `start` if present (e.g. lists pasted in starting at 5)
            // — falls back to undefined which renders as the default `1`.
            const start = typeof node.attrs?.start === "number" ? node.attrs.start : undefined;
            return (_jsx("ol", { start: start, children: children }, key));
        }
        case "listItem":
            return _jsx("li", { children: children }, key);
        case "taskList":
            // Tiptap's TaskList serialises as `<ul data-type="taskList">` —
            // we mirror that so the SCSS rules in `view.scss` (and the editor's
            // scoped equivalent in `omnibar-host.scss`) can target the
            // attribute selector and apply the no-bullet, checkbox-aware
            // layout that distinguishes task lists from regular bullet lists.
            return (_jsx("ul", { "data-type": "taskList", children: children }, key));
        case "taskItem": {
            // `checked: boolean` is the standard TaskItem attribute. When
            // the consumer wires `onToggleTask`, the checkbox is INTERACTIVE
            // — the input drops `disabled`/`readOnly` and an `onChange`
            // handler fires the callback with this task item's
            // document-order index plus the next checked state. Without the
            // callback the checkbox stays `disabled` (the read-only default,
            // used by the dev showcase and any surface that doesn't have a
            // persistence path).
            //
            // Note `taskIndex` is the ordinal allocated in `renderNode`
            // above; it's stable across consumers that walk the same `doc`
            // in depth-first document order (see `RenderContext` rationale).
            // The live Tiptap editor in the omnibar paints its own
            // interactive checkbox via the TaskItem NodeView, separate from
            // this render path.
            const checked = node.attrs?.checked === true;
            const interactive = ctx.onToggleTask !== undefined && taskIndex !== undefined;
            const handleChange = interactive
                ? (e) => {
                    ctx.onToggleTask?.(taskIndex, e.currentTarget.checked);
                }
                : undefined;
            const handleMouseDown = interactive
                ? (e) => {
                    // Cards above this checkbox bind a drag gesture on
                    // mousedown (see `block-view-container.tsx`); without
                    // stopping propagation a quick click would arm the drag
                    // before the click resolved, and the click-vs-drag
                    // tie-break would swallow the toggle. Per the widget
                    // rule in `.cursor/rules/widget-design.mdc`: interactive
                    // elements inside cards must stopPropagation on
                    // mousedown.
                    e.stopPropagation();
                }
                : undefined;
            return (_jsx("li", { "data-type": "taskItem", "data-checked": checked ? "true" : undefined, children: _jsxs("label", { children: [_jsx("input", { type: "checkbox", checked: checked, disabled: interactive === false, readOnly: interactive === false, onChange: handleChange, onMouseDown: handleMouseDown }), _jsx("span", { children: children })] }) }, key));
        }
        case "blockquote":
            return _jsx("blockquote", { children: children }, key);
        case "codeBlock": {
            // StarterKit's codeBlock carries an optional `language` attr — we
            // surface it via the standard `language-*` class so any future
            // syntax-highlighter (e.g. Prism, Shiki) can hook in without
            // schema changes.
            const lang = typeof node.attrs?.language === "string" ? node.attrs.language : undefined;
            return (_jsx("pre", { children: _jsx("code", { className: lang ? `language-${lang}` : undefined, children: children }) }, key));
        }
        default:
            // Unknown nodes pass through their children. Lost structural
            // semantics, preserved text content. See the file header for why.
            return _jsx(React.Fragment, { children: children }, key);
    }
}
/**
 * True when a paragraph has no rendered content — no `content` array,
 * an empty array, or only empty text nodes. The editor's resting state
 * and any "blank line between thoughts" the user types both serialise
 * to one of these shapes; both need the `<br>` filler treatment in the
 * read-only render to paint as a visible blank line.
 *
 * A paragraph that contains a `hardBreak` or any non-text inline node
 * is NOT considered empty — those produce visible output of their own.
 */
function isEmptyParagraph(node) {
    if (!node.content || node.content.length === 0)
        return true;
    return node.content.every((child) => child.type === "text" && (child.text === undefined || child.text.length === 0));
}
// ---------------------------------------------------------------------------
// Text + marks
// ---------------------------------------------------------------------------
/**
 * Wraps a text node's content in nested mark elements. Marks compose by
 * order — `[bold, italic]` becomes `<em><strong>text</strong></em>` (the
 * outer mark wraps the inner). Order doesn't matter visually for the
 * built-in marks, but we wrap left-to-right for predictability.
 *
 * Only the marks we recognise are emitted; unknown marks are silently
 * dropped (their wrapped text still renders).
 */
function renderTextWithMarks(node, key) {
    const text = node.text ?? "";
    if (!node.marks || node.marks.length === 0) {
        return _jsx(React.Fragment, { children: text }, key);
    }
    let wrapped = text;
    for (const mark of node.marks) {
        wrapped = wrapMark(mark, wrapped);
    }
    return _jsx(React.Fragment, { children: wrapped }, key);
}
function wrapMark(mark, children) {
    switch (mark.type) {
        case "bold":
            return _jsx("strong", { children: children });
        case "italic":
            return _jsx("em", { children: children });
        case "code":
            return _jsx("code", { children: children });
        case "strike":
            return _jsx("s", { children: children });
        case "underline":
            return _jsx("u", { children: children });
        case "link": {
            const href = typeof mark.attrs?.href === "string" ? mark.attrs.href : undefined;
            if (!href)
                return _jsx(_Fragment, { children: children });
            // Defense-in-depth XSS guard: even though `TiptapMarkSchema`
            // rejects link marks with `javascript:`/`data:`/`vbscript:` hrefs
            // at ingest time, we re-check at render time so legacy docs
            // captured before that schema shipped (and any future schema
            // bypass) can't paint a live `<a href="javascript:...">` onto
            // a memory card. Drop the link, keep the text.
            if (isSafeLinkHref(href) === false)
                return _jsx(_Fragment, { children: children });
            // Open in a new tab + drop opener context. Matches what users
            // expect from links on a memory card and mirrors the editor's
            // Link extension defaults.
            return (_jsx("a", { href: href, target: "_blank", rel: "noopener noreferrer", children: children }));
        }
        default:
            return _jsx(_Fragment, { children: children });
    }
}
